import React, { useState, useEffect, useCallback } from 'react';

export interface PaginatedState<T> {
  data: T[];
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  loading: boolean;
  error: string;
  fetch: (page: number) => Promise<void>;
  setPage: (page: number) => void;
  handlePageChange: (newPage: number, sectionSelector?: string) => void;
  getPageNumbers: () => number[];
  setData: React.Dispatch<React.SetStateAction<T[]>>;
  resetPagination: () => void;
  reset: () => void;
}

interface UsePaginatedFetchOptions<T, R> {
  fetchUrl: (page: number, limit: number) => string;
  perPage: number;
  token: string;
  extractData: (response: R) => T[];
  /**
   * Pattern 1 (opaque pagination): the API returns only the items array.
   * If the array length equals perPage, we assume a next page exists.
   * totalPages is tracked as the highest page reached (+1 if full).
   */
  inferHasNext?: boolean;
  /**
   * Pattern 2 (total-based pagination): the API returns total + has_next.
   * totalPages is computed from total / perPage.
   */
  totalBased?: {
    extractTotal: (response: R) => number;
    extractHasNext: (response: R) => boolean;
    extractHasPrev?: (response: R) => boolean;
  };
  /** Optional side-effect when a response is successfully received. */
  onResponse?: (response: R) => void;
  /** Custom 422 error extractor. Defaults to joining detail[].msg. */
  parse422?: (raw: any) => string;
  enabled?: boolean;
}

export function usePaginatedFetch<T, R = any>(
  options: UsePaginatedFetchOptions<T, R>
): PaginatedState<T> {
  const {
    fetchUrl,
    perPage,
    token,
    extractData,
    inferHasNext = false,
    totalBased,
    onResponse,
    parse422,
    enabled = true,
  } = options;

  const [data, setData] = useState<T[]>([]);
  const [page, setPageState] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchPage = useCallback(async (pageNum: number) => {
    if (!token) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(fetchUrl(pageNum, perPage), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const raw: R = await response.json();
        const items = extractData(raw);
        setData(items);
        onResponse?.(raw);

        if (totalBased) {
          const total = totalBased.extractTotal(raw);
          const hasNext = totalBased.extractHasNext(raw);
          const hasPrev = totalBased.extractHasPrev ? totalBased.extractHasPrev(raw) : page > 1;
          setHasNextPage(hasNext);
          setHasPrevPage(hasPrev);
          setTotalPages(Math.max(1, Math.ceil(total / perPage)));
        } else if (inferHasNext) {
          const full = items.length === perPage;
          setHasNextPage(full);
          setTotalPages(prev => Math.max(prev, pageNum + (full ? 1 : 0)));
        }
      } else if (response.status === 401 || response.status === 403) {
        setError('Session expired or access denied. Please log out and log in again.');
      } else if (response.status === 422) {
        const data = await response.json().catch(() => ({}));
        const messages = parse422
          ? parse422(data)
          : Array.isArray(data.detail)
            ? data.detail.map((d: { msg: string }) => d.msg).join('. ')
            : 'Invalid request.';
        setError(messages);
      } else {
        setError(`Failed to load (${response.status}). Please try again.`);
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUrl, perPage, token, extractData, inferHasNext, totalBased, onResponse, parse422]);

  useEffect(() => {
    if (enabled && token) {
      fetchPage(page);
    }
  }, [enabled, token, page, fetchPage]);

  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const handlePageChange = useCallback((newPage: number, sectionSelector?: string) => {
    setPageState(newPage);
    if (sectionSelector) {
      const section = document.querySelector(sectionSelector);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, []);

  const getPageNumbers = useCallback((): number[] => {
    const delta = 2;
    const range: number[] = [];
    const left = Math.max(1, page - delta);
    const right = Math.min(totalPages, page + delta);
    for (let i = left; i <= right; i++) {
      range.push(i);
    }
    return range;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages]);

  const resetPagination = useCallback(() => {
    setPageState(1);
    setTotalPages(1);
    setHasNextPage(false);
    setHasPrevPage(false);
  }, []);

  const reset = useCallback(() => {
    setData([]);
    setPageState(1);
    setTotalPages(1);
    setHasNextPage(false);
    setHasPrevPage(false);
    setError('');
  }, []);

  return {
    data,
    page,
    totalPages,
    hasNextPage,
    hasPrevPage,
    loading,
    error,
    fetch: fetchPage,
    setPage,
    handlePageChange,
    getPageNumbers,
    setData,
    resetPagination,
    reset,
  };
}
